import { Box, Flex, Text, Heading, SimpleGrid } from '@chakra-ui/react'
import { TrendingUp, TrendingDown, Users, AlertTriangle, Wallet } from 'lucide-react'
import { useOwnerAccountStats } from '../hooks'

interface AccountingStatsProps {
  residenceId: string
}

export function AccountingStats({ residenceId }: AccountingStatsProps) {
  const { data: stats, isLoading } = useOwnerAccountStats(residenceId)

  if (isLoading) {
    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} p={5} bg="white" borderRadius="lg" boxShadow="sm" h="100px" />
        ))}
      </SimpleGrid>
    )
  }

  const statCards = [
    {
      label: 'Comptes copropriétaires',
      value: stats?.totalAccounts || 0,
      icon: Users,
      color: 'blue',
      format: 'number',
    },
    {
      label: 'Solde total',
      value: stats?.totalBalance ?? 0,
      icon: (stats?.totalBalance ?? 0) >= 0 ? TrendingUp : TrendingDown,
      color: (stats?.totalBalance ?? 0) >= 0 ? 'green' : 'red',
      format: 'currency',
    },
    {
      label: 'Impayés',
      value: stats?.unpaidCount ?? 0,
      icon: AlertTriangle,
      color: (stats?.unpaidCount ?? 0) > 0 ? 'orange' : 'green',
      format: 'number',
      suffix: (stats?.unpaidCount ?? 0) === 1 ? ' copropriétaire' : ' copropriétaires',
    },
    {
      label: 'Montant impayés',
      value: stats?.unpaidAmount ?? 0,
      icon: Wallet,
      color: (stats?.unpaidAmount ?? 0) > 0 ? 'red' : 'green',
      format: 'currency',
    },
  ]

  const formatValue = (value: number, format: string, suffix?: string) => {
    if (format === 'currency') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(value)
    }
    return `${value}${suffix || ''}`
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
      {statCards.map((stat, index) => {
        const Icon = stat.icon
        return (
          <Box
            key={index}
            p={5}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.100"
          >
            <Flex justify="space-between" align="start">
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  {stat.label}
                </Text>
                <Heading size="lg" color={`${stat.color}.600`}>
                  {formatValue(stat.value, stat.format, stat.suffix)}
                </Heading>
              </Box>
              <Box
                p={2}
                borderRadius="lg"
                bg={`${stat.color}.50`}
                color={`${stat.color}.500`}
              >
                <Icon size={20} />
              </Box>
            </Flex>
          </Box>
        )
      })}
    </SimpleGrid>
  )
}
